// src/context/MyVendorsContext.js
// Global context for the user's uploaded vendor lists (Enterprise feature).
// Simpler than MyProvidersContext — no BigQuery NPI sync needed here, since
// vendor matching only produces company_id references that BigQuery already
// has natively. No cross-page filter yet (unlike My Providers); this context
// currently just manages list state for MyVendorsPage. The conflict report
// (a later step) will consume this context's confirmed company_id set.

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useUser, useAuth } from "@clerk/react";
import { useSubscription } from "./SubscriptionContext";
import { authedFetch } from "../utils/apiClient";

const MyVendorsContext = createContext(null);

export function MyVendorsProvider({ children }) {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { isEnterprise } = useSubscription();

  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !user || !isEnterprise) {
      setLists([]);
      setActiveListId(null);
      return;
    }
    loadLists();
  }, [isSignedIn, user?.id, isEnterprise]);

  const loadLists = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await authedFetch(getToken, `/api/user-data?action=getVendorLists&userId=${user.id}`);
      const data = await res.json();
      const fetchedLists = data.lists || [];
      setLists(fetchedLists);
      setActiveListId((prev) => {
        if (prev && fetchedLists.some((l) => l.id === prev)) return prev;
        return fetchedLists[0]?.id || null;
      });
    } catch {
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, getToken]);

  const refreshLists = useCallback(() => loadLists(), [loadLists]);

  const deleteList = useCallback(async (listId) => {
    if (!user) return { success: false };
    try {
      const res = await authedFetch(getToken, "/api/user-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "deleteVendorList", userId: user.id, listId }),
      });
      const data = await res.json();
      if (data.success) await loadLists();
      return data;
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  }, [user?.id, loadLists, getToken]);

  const activeList = lists.find((l) => l.id === activeListId) || null;

  return (
    <MyVendorsContext.Provider
      value={{ lists, loading, activeList, activeListId, setActiveListId, refreshLists, deleteList }}
    >
      {children}
    </MyVendorsContext.Provider>
  );
}

export function useMyVendors() {
  return useContext(MyVendorsContext);
}